import { useEffect, useState } from 'react';
import { Badge, Card, ErrorMessage, Field, Loading, Modal, ModuleHeader, Tabs, useAsync, useToast } from '../../components/ui';
import { IconMonitor, IconPlus, IconEdit, IconTrash } from '../../components/icons';
import { api } from '../../lib/api';

type FieldMode = 'hidden' | 'optional' | 'required';
type CustomFieldType = 'text' | 'textarea' | 'select';

interface PortalCustomField {
  key: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  options?: string[];
  sortOrder: number;
}

type PortalLocale = 'en' | 'fr' | 'es' | 'it';

interface PortalTranslatable {
  title?: string;
  intro?: string;
  privacyNotice?: string;
  confirmationText?: string;
  customFieldLabels?: Record<string, string>;
}

interface PortalConfig {
  title: string;
  intro: string;
  privacyNotice: string;
  confirmationText: string;
  posterInstruction: string;
  fields: {
    description: FieldMode;
    locationDetail: FieldMode;
    category: FieldMode;
    photos: FieldMode;
  };
  customFields: PortalCustomField[];
  translations?: Partial<Record<PortalLocale, PortalTranslatable>>;
}

const FIELD_LABELS: Record<keyof PortalConfig['fields'], string> = {
  description: 'Details / Beschreibung',
  locationDetail: 'Ort im Objekt',
  category: 'Gewerk / Kategorie',
  photos: 'Fotos',
};

const LANGUAGE_TABS: { key: 'de' | PortalLocale; label: string }[] = [
  { key: 'de', label: 'Deutsch' },
  { key: 'en', label: 'Englisch' },
  { key: 'fr', label: 'Französisch' },
  { key: 'es', label: 'Spanisch' },
  { key: 'it', label: 'Italienisch' },
];

const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: 'Text (einzeilig)',
  textarea: 'Text (mehrzeilig)',
  select: 'Auswahl',
};

/**
 * Konfiguration der öffentlichen Meldeseite (`/melden/:token`, Kap. 5).
 *
 * Dieselben Texte und Feldeinstellungen gelten für jeden Aufruf über einen
 * QR-Code – die Seite selbst entscheidet nicht objektweise, nur mandantenweit.
 * Die Vorschau rechts zeigt ungefähr, was ein Melder ohne Benutzerkonto sieht.
 *
 * Benutzerdefinierte Felder gelten kanalübergreifend: Dieselbe Liste wird auf
 * der Portalseite gezeigt und von den Bot-Dialogen (Telegram, WhatsApp,
 * Web-Widget) abgefragt – eine Änderung hier wirkt überall, ohne dass jeder
 * Kanal einzeln nachgezogen werden muss.
 */
export function PortalSettings() {
  const { notify } = useToast();
  const data = useAsync(() => api.get('/api/catalogs/portal-config'), []);
  const [form, setForm] = useState<PortalConfig | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [fieldModal, setFieldModal] = useState<PortalCustomField | 'new' | null>(null);
  const [locale, setLocale] = useState<'de' | PortalLocale>('de');

  useEffect(() => {
    if (data.data?.config) setForm(data.data.config);
  }, [data.data]);

  if (data.loading || !form) return <Loading />;

  const setField = (key: keyof PortalConfig['fields'], mode: FieldMode) =>
    setForm((current) => (current ? { ...current, fields: { ...current.fields, [key]: mode } } : current));

  type TranslatableTextKey = 'title' | 'intro' | 'privacyNotice' | 'confirmationText';
  const translation = locale === 'de' ? undefined : form.translations?.[locale];
  const textValue = (key: TranslatableTextKey) => (locale === 'de' ? form[key] : translation?.[key] ?? '');
  const setTextValue = (key: TranslatableTextKey, value: string) =>
    setForm((current) => {
      if (!current) return current;
      if (locale === 'de') return { ...current, [key]: value };
      return {
        ...current,
        translations: {
          ...current.translations,
          [locale]: { ...current.translations?.[locale], [key]: value },
        },
      };
    });
  const setCustomFieldLabelTranslation = (key: string, value: string) =>
    setForm((current) => {
      if (!current || locale === 'de') return current;
      return {
        ...current,
        translations: {
          ...current.translations,
          [locale]: {
            ...current.translations?.[locale],
            customFieldLabels: { ...current.translations?.[locale]?.customFieldLabels, [key]: value },
          },
        },
      };
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put('/api/catalogs/portal-config', form);
      notify('Portalseite gespeichert.', 'success');
      data.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const customFields = [...form.customFields].sort((a, b) => a.sortOrder - b.sortOrder);

  const saveCustomField = (field: PortalCustomField, previousKey?: string) => {
    setForm((current) => {
      if (!current) return current;
      const withoutPrevious = current.customFields.filter((entry) => entry.key !== previousKey);
      return { ...current, customFields: [...withoutPrevious, field] };
    });
    setFieldModal(null);
  };

  const deleteCustomField = (key: string, label: string) => {
    if (!window.confirm(`Feld „${label}“ löschen? Bereits gemeldete Aufträge behalten ihre gespeicherte Antwort.`)) return;
    setForm((current) => (current ? { ...current, customFields: current.customFields.filter((entry) => entry.key !== key) } : current));
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconMonitor width={22} height={22} />}
        title="Portalseite für Meldungen von Dritten"
        subtitle="Texte und Felder der öffentlichen Meldeseite, die über den QR-Code am Objekt erreichbar ist – ohne Anmeldung."
        pill="Konfiguration"
        tone="info"
      />

      <ErrorMessage error={error} />

      <div className="detail-layout">
        <div className="stack">
          <Card
            title="Texte"
            subtitle={locale === 'de' ? undefined : 'Leer gelassene Felder zeigen weiterhin den deutschen Text.'}
          >
            <div className="stack">
              <Tabs active={locale} onChange={(key) => setLocale(key as 'de' | PortalLocale)} tabs={LANGUAGE_TABS} />
              <Field label="Titel">
                <input
                  value={textValue('title')}
                  placeholder={locale === 'de' ? undefined : form.title}
                  onChange={(event) => setTextValue('title', event.target.value)}
                />
              </Field>
              <Field label="Einleitungstext" hint="Erscheint unter dem Objektnamen.">
                <textarea
                  value={textValue('intro')}
                  placeholder={locale === 'de' ? undefined : form.intro}
                  onChange={(event) => setTextValue('intro', event.target.value)}
                />
              </Field>
              <Field label="Datenschutzhinweis" hint="Erscheint über dem Absenden-Knopf.">
                <textarea
                  value={textValue('privacyNotice')}
                  placeholder={locale === 'de' ? undefined : form.privacyNotice}
                  onChange={(event) => setTextValue('privacyNotice', event.target.value)}
                />
              </Field>
              <Field label="Bestätigungstext" hint="Erscheint nach dem Absenden.">
                <textarea
                  value={textValue('confirmationText')}
                  placeholder={locale === 'de' ? undefined : form.confirmationText}
                  onChange={(event) => setTextValue('confirmationText', event.target.value)}
                />
              </Field>
              {locale !== 'de' && customFields.length > 0 && (
                <Field label="Feldbezeichnungen der Zusatzfelder">
                  <div className="stack" style={{ gap: 6 }}>
                    {customFields.map((customField) => (
                      <div className="row" style={{ gap: 8, alignItems: 'center' }} key={customField.key}>
                        <span className="small subtle" style={{ minWidth: 140 }}>{customField.label}</span>
                        <input
                          style={{ flex: 1 }}
                          value={translation?.customFieldLabels?.[customField.key] ?? ''}
                          placeholder={customField.label}
                          onChange={(event) => setCustomFieldLabelTranslation(customField.key, event.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </Field>
              )}
            </div>
          </Card>

          <Card title="Druckblatt" subtitle="Text auf dem gedruckten QR-Aushang – bewusst einsprachig, ein Aushang hängt physisch an einem Ort.">
            <Field label="Scan-Hinweissatz" hint="Erscheint auf dem Aushang unter der Objektadresse, über dem QR-Code.">
              <textarea
                value={form.posterInstruction}
                onChange={(event) => setForm({ ...form, posterInstruction: event.target.value })}
              />
            </Field>
          </Card>

          <Card title="Felder" subtitle="Kontaktdaten des Melders werden über den Meldekanal „QR-Code am Objekt“ geregelt, nicht hier.">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Feld</th>
                    <th>Ausgeblendet</th>
                    <th>Optional</th>
                    <th>Pflicht</th>
                  </tr>
                </thead>
                <tbody>
                  {(Object.keys(FIELD_LABELS) as (keyof PortalConfig['fields'])[]).map((key) => (
                    <tr key={key}>
                      <td>{FIELD_LABELS[key]}</td>
                      {(['hidden', 'optional', 'required'] as FieldMode[]).map((mode) => (
                        <td key={mode}>
                          <input
                            type="radio"
                            name={`field-${key}`}
                            checked={form.fields[key] === mode}
                            onChange={() => setField(key, mode)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card
            title="Benutzerdefinierte Felder"
            subtitle="Gelten für alle Kanäle – Portalseite, Telegram, WhatsApp und Web-Widget"
            actions={
              <button className="btn primary sm" onClick={() => setFieldModal('new')}>
                <IconPlus width={14} height={14} /> Feld hinzufügen
              </button>
            }
            noBody
          >
            {customFields.length === 0 ? (
              <div className="small subtle" style={{ padding: '4px 16px 16px' }}>
                Noch keine benutzerdefinierten Felder angelegt.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Bezeichnung</th>
                      <th>Typ</th>
                      <th>Pflicht</th>
                      <th className="actions">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customFields.map((customField) => (
                      <tr key={customField.key}>
                        <td>
                          <strong>{customField.label}</strong>
                          <div className="small subtle mono">{customField.key}</div>
                        </td>
                        <td>
                          {CUSTOM_FIELD_TYPE_LABELS[customField.type]}
                          {customField.type === 'select' && customField.options?.length ? (
                            <div className="small subtle">{customField.options.join(', ')}</div>
                          ) : null}
                        </td>
                        <td>{customField.required ? <Badge variant="info">Pflicht</Badge> : <span className="small subtle">optional</span>}</td>
                        <td className="actions">
                          <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                            <button className="btn sm ghost" onClick={() => setFieldModal(customField)} title={`„${customField.label}“ bearbeiten`}>
                              <IconEdit width={13} height={13} />
                            </button>
                            <button className="btn sm ghost" onClick={() => deleteCustomField(customField.key, customField.label)} title={`„${customField.label}“ löschen`}>
                              <IconTrash width={13} height={13} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="row end">
            <button className="btn primary" onClick={save} disabled={busy}>
              {busy ? 'Wird gespeichert …' : 'Speichern'}
            </button>
          </div>
        </div>

        <div className="stack">
          <Card title="Vorschau" subtitle="Ungefähre Ansicht der Meldeseite">
            <div className="stack" style={{ gap: 10 }}>
              <div>
                <strong>{form.title || 'Mängel melden'}</strong>
                {form.intro && <p className="small subtle" style={{ marginTop: 4 }}>{form.intro}</p>}
              </div>
              <div className="stack" style={{ gap: 6 }}>
                <div className="small">Kurzbeschreibung *</div>
                {form.fields.description !== 'hidden' && (
                  <div className="small subtle">Details{form.fields.description === 'required' ? ' *' : ''}</div>
                )}
                {form.fields.locationDetail !== 'hidden' && (
                  <div className="small subtle">Ort im Objekt{form.fields.locationDetail === 'required' ? ' *' : ''}</div>
                )}
                {form.fields.category !== 'hidden' && (
                  <div className="small subtle">Gewerk{form.fields.category === 'required' ? ' *' : ''}</div>
                )}
                {form.fields.photos !== 'hidden' && (
                  <div className="small subtle">Fotos{form.fields.photos === 'required' ? ' *' : ' (optional)'}</div>
                )}
                {customFields.map((customField) => (
                  <div className="small subtle" key={customField.key}>
                    {customField.label}
                    {customField.required ? ' *' : ' (optional)'}
                  </div>
                ))}
              </div>
              {form.privacyNotice && <p className="small subtle">{form.privacyNotice}</p>}
              <div className="alert success" style={{ fontSize: 12.5 }}>
                {form.confirmationText || 'Vielen Dank! Ihre Meldung wurde erfasst.'}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {fieldModal && (
        <CustomFieldModal
          field={fieldModal === 'new' ? undefined : fieldModal}
          existingKeys={form.customFields.map((entry) => entry.key)}
          nextSortOrder={form.customFields.length}
          onClose={() => setFieldModal(null)}
          onSave={saveCustomField}
        />
      )}
    </div>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[äöü]/g, (match) => ({ ä: 'ae', ö: 'oe', ü: 'ue' })[match] ?? match)
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function CustomFieldModal({
  field,
  existingKeys,
  nextSortOrder,
  onClose,
  onSave,
}: {
  field?: PortalCustomField;
  existingKeys: string[];
  nextSortOrder: number;
  onClose: () => void;
  onSave: (field: PortalCustomField, previousKey?: string) => void;
}) {
  const [label, setLabel] = useState(field?.label ?? '');
  const [key, setKey] = useState(field?.key ?? '');
  const [type, setType] = useState<CustomFieldType>(field?.type ?? 'text');
  const [required, setRequired] = useState(!!field?.required);
  const [optionsText, setOptionsText] = useState((field?.options ?? []).join('\n'));

  const effectiveKey = field ? field.key : key || slugify(label);
  const keyTaken = !field && existingKeys.includes(effectiveKey);
  const options = optionsText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const canSave = label.trim().length > 0 && effectiveKey.length > 0 && !keyTaken && (type !== 'select' || options.length > 0);

  return (
    <Modal
      title={field ? `Feld bearbeiten – ${field.label}` : 'Benutzerdefiniertes Feld anlegen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!canSave}
            onClick={() =>
              onSave(
                {
                  key: effectiveKey,
                  label: label.trim(),
                  type,
                  required,
                  options: type === 'select' ? options : undefined,
                  sortOrder: field?.sortOrder ?? nextSortOrder,
                },
                field?.key,
              )
            }
          >
            Übernehmen
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Bezeichnung" required hint="Erscheint als Frage auf der Portalseite und in den Bot-Dialogen.">
          <input value={label} onChange={(event) => setLabel(event.target.value)} autoFocus />
        </Field>
        <Field
          label="Schlüssel"
          required
          hint={field ? 'Nach dem Anlegen unveränderlich.' : keyTaken ? 'Dieser Schlüssel wird bereits verwendet.' : 'Wird automatisch aus der Bezeichnung abgeleitet, lässt sich aber anpassen.'}
        >
          <input
            value={effectiveKey}
            disabled={!!field}
            onChange={(event) => setKey(slugify(event.target.value))}
          />
        </Field>
        <Field label="Typ">
          <select value={type} onChange={(event) => setType(event.target.value as CustomFieldType)}>
            {(Object.keys(CUSTOM_FIELD_TYPE_LABELS) as CustomFieldType[]).map((option) => (
              <option key={option} value={option}>
                {CUSTOM_FIELD_TYPE_LABELS[option]}
              </option>
            ))}
          </select>
        </Field>
        {type === 'select' && (
          <Field label="Optionen" required hint="Eine Option je Zeile.">
            <textarea value={optionsText} onChange={(event) => setOptionsText(event.target.value)} rows={4} />
          </Field>
        )}
        <label className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={required} onChange={(event) => setRequired(event.target.checked)} />
          Pflichtfeld
        </label>
      </div>
    </Modal>
  );
}
